/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/stockNotifications.scss';

import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {NotificationProps, NotificationTypeProps} from '../notificationsPage';

import useDefaultNotification from '../useDefaultNotification';

import NotificationProducts from '../notificationComponents/notificationProducts';

import NotificationEmail from '../notificationComponents/notificationEmail';

import NotificationActions from '../notificationComponents/notificationActions';

import Notification from '../../../models/notificationModel';

import {NotificationType} from '../../../util/enums';

import {AwesomeIcon} from '@dvrd/dvr-controls';

import NotificationIntervalComp from '../notificationComponents/notificationInterval';

import classNames from 'classnames';

import BolmateBadgePremium from '../bolmateBadgePremium';

import NotificationInputController from '../notificationComponents/notificationInput/notificationInputController';

function StockNotification(props: NotificationProps) {
    const {t} = useTranslation();
    const {
        notification,
        loading,
        onChangeNotification,
        onDeleteNotification,
        onSendTestNotification,
        disabled,
        canDelete,
        canEdit,
        onReplaceNotification
    } = props;
    const [onChangeEmailAddresses] = useDefaultNotification(props);

    function onChangeThreshold(value: number) {
        const config = notification.stockConfig;
        config.threshold = value;
        notification.flex = config.dump();
        onChangeNotification(notification);
    }

    function renderThreshold() {
        return (
            <section>
                <label className='fixed-text'>
                    {t('notifications.stock.thresholdLabel', 'Bij een voorraad minder dan')}
                </label>
                <NotificationInputController
                    onSubmit={onChangeThreshold}
                    value={notification.stockConfig.threshold}
                    label={t('notifications.stock.thresholdInputLabel', 'Voorraad minder dan')}
                    disabled={loading || disabled || !canEdit}
                    inputProps={{
                        wholeNumbers: true,
                        disableSymbols: true,
                        inputProps: {min: 0}
                    }}
                />
            </section>
        );
    }

    return (
        <>
            <div className={classNames('stock-notification notification-config', disabled && 'disabled')}>
                <label className='notification-title'>
                    <AwesomeIcon name='box-open' className='type-icon' />{' '}
                    {t('notifications.stock.title', 'Voorraad notificatie')} <BolmateBadgePremium />
                </label>
                <div className='notification'>
                    {renderThreshold()}
                    <NotificationProducts
                        onSuccess={onReplaceNotification}
                        notification={notification}
                        disabled={loading || disabled || !canEdit}
                    />
                    <NotificationEmail
                        onChangeEmailAddresses={onChangeEmailAddresses}
                        emailAddresses={notification.emailAddresses}
                        disabled={loading || disabled || !canEdit}
                    />
                    <NotificationIntervalComp
                        onChangeNotification={onChangeNotification}
                        disabled={loading || disabled || !canEdit}
                        notification={notification}
                        withRealtime
                    />
                </div>
                <NotificationActions
                    onDeleteNotification={onDeleteNotification}
                    onSendTestNotification={onSendTestNotification}
                    disabled={disabled}
                    notification={notification}
                    canDelete={canDelete}
                />
            </div>
        </>
    );
}

export default function StockNotifications(props: NotificationTypeProps) {
    const {notifications, moduleActive} = props;
    const stockNotifications = useMemo(() => {
        return notifications.filter(
            (notification: Notification) => notification.notificationType === NotificationType.STOCK
        );
    }, [notifications]);
    if (!stockNotifications.length) return null;

    function renderNotification(notification: Notification, index: number) {
        return <StockNotification key={index} notification={notification} disabled={!moduleActive} {...props} />;
    }

    return (
        <div className='notification-type stock-notifications'>
            <div className='notifications'>{stockNotifications.map(renderNotification)}</div>
        </div>
    );
}
